'use client';

import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store/store';
import { useSnackbar } from 'notistack';
import {
  useGetAllServiceProviderBookingsQuery,
  useUpdateServiceProviderBookingStatusMutation,
  useCancelServiceProviderBookingMutation,
} from '@/services/ServiceProviderBookingService';
import { BookingStatus } from '@/types/booking-management.types';
import { bookingConfig } from '@/config';
import EventModal from '../../habor-management/components/EventModal';
import DayEventsModal from '../../habor-management/components/DayEventsModal';
import {
  type CalendarEvent,
  mapBookingStatus,
  type ViewType,
  statusConfig,
} from '../../habor-management/components/types';
import FilterChip from '../../habor-management/components/FilterChip';
import {
  LoadingState,
  NoEventsState,
  NoHarborState,
} from '../../habor-management/components/NoDataStates';
import CalendarView from '../../habor-management/components/CalendarView';
import { useNavigate } from 'react-router-dom';
import { CalendarIcon, TrendingUp, Activity, Filter, X } from 'lucide-react';
import { formatDateWithOptions } from '@/utils/Date';

const formatDateDay = (d: Date) => d.toISOString().substring(0, 10);

const QUERY_LIMIT = bookingConfig.QUERY_LIMIT;
const POLLING_INTERVAL_MS = bookingConfig.POLL_INTERVAL_MS;

export default function ServiceProviderAvailabilityCalendar() {
  const serviceProviderId = useSelector(
    (state: RootState) => state.auth.selectedServiceProviderId
  );
  const { enqueueSnackbar } = useSnackbar();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<ViewType>('month');
  const [statusFilters, setStatusFilters] = useState<Set<string>>(
    new Set([
      'confirmed',
      'payment-pending',
      'pending-confirmation',
      'completed',
      'cancelled',
    ])
  );
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null
  );
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dayEventsModal, setDayEventsModal] = useState<{
    open: boolean;
    date?: Date;
    events?: CalendarEvent[];
  }>({ open: false });
  const [userFilter, setUserFilter] = useState<string>('');

  const { data: bookingsResponse, isLoading: isBookingsLoading } =
    useGetAllServiceProviderBookingsQuery(
      {
        page: 1,
        limit: QUERY_LIMIT,
        service_provider_id: serviceProviderId || undefined,
        search: userFilter || undefined,
      },
      { skip: !serviceProviderId, pollingInterval: POLLING_INTERVAL_MS }
    );

  const [updateBookingStatus, { isLoading: isCancelling }] =
    useUpdateServiceProviderBookingStatusMutation();
  const [cancelBooking, { isLoading: isCancellingConfirmed }] =
    useCancelServiceProviderBookingMutation();
  const [cancelReason, setCancelReason] = useState('');

  const backendEvents: CalendarEvent[] = useMemo(() => {
    const data = bookingsResponse?.data;
    if (!data) return [];

    return data.map((b) => ({
      id: b.id,
      title: `${b.service?.name || 'Service'} - ${b.user?.name || b.user?.email}`,
      start: b.start_date.split('T')[0],
      end: b.end_date.split('T')[0],
      status: mapBookingStatus(b.status),
      berthNumber: '-',
      user: {
        name: b.user?.name || b.user?.email || 'Guest',
        email: b.user?.email || '',
      },
      isConflicting: b.isConflicting,
      assetType: undefined,
      service: b.service
        ? {
            id: b.service.id,
            name: b.service.name,
            description: b.service.description ?? '',
            category: b.service.category ?? '',
            sub_category: b.service.sub_category ?? '',
            service_type: b.service.service_type ?? '',
            language: 'en',
            source_language: 'en',
          }
        : undefined,
      price: b.total_price,
      quantity: b.quantity,
      startTime: b.start_time || undefined,
      endTime: b.end_time || undefined,
    }));
  }, [bookingsResponse]);

  const filteredEvents = useMemo(
    () =>
      backendEvents.filter((ev) => {
        if (!statusFilters.has(ev.status)) return false;

        if (
          userFilter &&
          !(
            ev.user?.name?.toLowerCase().includes(userFilter.toLowerCase()) ||
            ev.user?.email?.toLowerCase().includes(userFilter.toLowerCase())
          )
        )
          return false;

        return true;
      }),
    [backendEvents, statusFilters, userFilter]
  );

  const toggleStatusFilter = (status: string) => {
    setStatusFilters((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setCancelReason('');
    setIsModalOpen(true);
  };

  const handleCancelBooking = async () => {
    if (!selectedEvent) return;
    try {
      if (selectedEvent.status === 'confirmed') {
        await cancelBooking({
          id: selectedEvent.id,
          data: {
            status: BookingStatus.CANCELLED,
            cancellation_reason: cancelReason || undefined,
          },
        }).unwrap();
      } else if (selectedEvent.status !== 'cancelled') {
        await updateBookingStatus({
          id: selectedEvent.id,
          update: {
            status: BookingStatus.CANCELLED,
            cancellation_reason: cancelReason || undefined,
          },
        }).unwrap();
      }
      enqueueSnackbar('Booking cancelled successfully', { variant: 'success' });
    } catch (e) {
      console.error('Failed to cancel booking', e);
      enqueueSnackbar('Failed to cancel booking', { variant: 'error' });
    } finally {
      setIsModalOpen(false);
    }
  };

  const getStatusCount = (status: string, useFiltered = false) =>
    (useFiltered ? filteredEvents : backendEvents).filter(
      (e) => e.status === status
    ).length;

  if (!serviceProviderId) {
    return <NoHarborState />;
  }

  const handleEditBooking = () => {
    navigate(`/service-bookings/${selectedEvent?.id}/edit`);
  };

  const handleViewBooking = () => {
    navigate(`/service-bookings/${selectedEvent?.id}`);
  };

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-4">
        {/* Status Cards - Only Total Bookings and Today's Bookings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Total Bookings Card */}
          <div className="group rounded-2xl shadow-lg hover:shadow-xl border border-gray-100 overflow-hidden">
            <div className="bg-blue-100 dark:bg-blue-950 contrast:bg-blue-950 p-4">
              <div className="flex justify-between items-start">
                <div className="text-gray-900">
                  <p className="text-gray-900 text-[16px] font-[500] ">
                    Total Bookings
                  </p>
                  <h1 className="text-2xl font-bold mt-1 text-gray-900">
                    {filteredEvents.length}
                  </h1>
                </div>
                <div className="p-3 bg-blue-300 dark:bg-blue-700 contrast:bg-blue-700 rounded-xl backdrop-blur-sm">
                  <CalendarIcon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">With current filters</span>
                <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400 contrast:text-blue-400">
                  <TrendingUp className="w-4 h-4" />
                  <span className="font-medium">Active</span>
                </div>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-600 h-2 rounded-full mt-3 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500"
                  style={{
                    width:
                      backendEvents.length > 0
                        ? `${Math.min(100, (filteredEvents.length / backendEvents.length) * 100)}%`
                        : '0%',
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* Today's Bookings Card */}
          <div className="group rounded-2xl shadow-lg hover:shadow-xl border border-gray-100 overflow-hidden">
            <div className="bg-blue-100 dark:bg-blue-950 contrast:bg-blue-950 p-4">
              <div className="flex justify-between items-start">
                <div className="text-gray-900">
                  <p className="text-gray-900 text-[16px] font-[500]">
                    Today's Bookings
                  </p>
                  <h1 className="text-2xl font-bold mt-1 text-gray-900">
                    {
                      filteredEvents.filter(
                        (e) => e.start === formatDateDay(new Date())
                      ).length
                    }
                  </h1>
                </div>
                <div className="p-3 bg-blue-300 dark:bg-blue-700 contrast:bg-blue-700 rounded-xl backdrop-blur-sm">
                  <Activity className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
            <div className="p-4">
              <div className="text-sm text-gray-600">
                {formatDateWithOptions(new Date(), {
                  weekday: 'long',
                  month: 'short',
                  day: 'numeric',
                }, 'en-US')}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-emerald-800 dark:text-emerald-400 contrast:text-emerald-400 font-medium">
                  Live Updates
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section - Only Status Filter and User Search */}
        <div className="mb-8">
          <div className="rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-blue-100 dark:bg-blue-950 contrast:bg-blue-950 px-6 py-4 border-b border-gray-100">
              <div className="flex flex-wrap gap-3 items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-300 dark:bg-blue-700 contrast:bg-blue-700 rounded-lg">
                    <Filter className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Filters & Search
                    </h2>
                    <p className="text-sm text-gray-600">
                      Customize your calendar view
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setStatusFilters(
                      new Set([
                        'confirmed',
                        'payment-pending',
                        'pending-confirmation',
                        'completed',
                        'cancelled',
                      ])
                    );
                    setUserFilter('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-600 contrast:text-black bg-white dark:bg-gray-100 hover:bg-gray-50 dark:hover:bg-gray-200 rounded-lg transition-colors duration-200 flex items-center gap-2 border border-gray-200 shadow-sm"
                >
                  <X className="w-4 h-4" />
                  Reset All
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Status Filter Chips */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Status Filters
                </h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(statusConfig)
                    .filter(
                      ([status]) =>
                        !['filled', 'available', 'blocked'].includes(status)
                    )
                    .map(([status, config]) => (
                      <FilterChip
                        key={status}
                        active={statusFilters.has(status)}
                        label={config.label}
                        mainColor={config.color}
                        lightColor={config.lightColor}
                        textColor={config.textColor}
                        borderColor={config.borderColor}
                        count={getStatusCount(status, true)}
                        onClick={() => toggleStatusFilter(status)}
                      />
                    ))}
                </div>
              </div>

              {/* User Filter */}
              <div>
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Search Users
                </span>
                <div className="relative">
                  <input
                    id="service-provider-user-search"
                    type="text"
                    placeholder="Search by name or email"
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                    className="w-full bg-light-active rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-3 focus:ring-blue-100 focus:border-blue-500 placeholder:text-gray-400 transition-all duration-200"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    {userFilter && (
                      <button
                        type="button"
                        onClick={() => setUserFilter('')}
                        className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors duration-200"
                        aria-label="Clear user search"
                      >
                        <X className="w-4 h-4" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Active Filters Display */}
              {(userFilter || statusFilters.size < 5) && (
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <div className="flex flex-wrap gap-2 items-center">
                    <span className="text-sm font-medium text-gray-600">
                      Active filters:
                    </span>

                    {userFilter && (
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gradient-to-r from-amber-100 dark:from-amber-900 to-yellow-100 dark:to-amber-900 text-amber-700 dark:text-amber-200 border border-amber-200 dark:border-amber-900">
                        {userFilter.length > 20
                          ? `${userFilter.substring(0, 20)}...`
                          : userFilter}
                        <button
                          type="button"
                          onClick={() => setUserFilter('')}
                          className="ml-2 text-amber-500 hover:text-amber-800 transition-colors duration-200"
                          aria-label="Remove user search filter"
                        >
                          <X className="w-3.5 h-3.5" aria-hidden="true" />
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Loading State */}
        <LoadingState isLoading={isBookingsLoading} />

        {/* Empty State */}
        {filteredEvents.length === 0 && !isBookingsLoading && (
          <NoEventsState
            onResetFilters={() => {
              setStatusFilters(
                new Set([
                  'confirmed',
                  'payment-pending',
                  'pending-confirmation',
                  'completed',
                  'cancelled',
                ])
              );
              setUserFilter('');
            }}
            isLoading={isBookingsLoading}
          />
        )}

        {/* Calendar View */}
        {filteredEvents.length > 0 && (
          <CalendarView
            currentDate={currentDate}
            view={view}
            setView={setView}
            filteredEvents={filteredEvents}
            handlePrevious={handlePrevious}
            handleNext={handleNext}
            handleToday={handleToday}
            handleEventClick={handleEventClick}
            onMoreClick={(date, events) =>
              setDayEventsModal({ open: true, date, events })
            }
          />
        )}

        {/* Modals */}
        <EventModal
          event={selectedEvent}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onCancel={handleCancelBooking}
          cancelReason={cancelReason}
          setCancelReason={setCancelReason}
          isSubmitting={isCancelling || isCancellingConfirmed}
          onEdit={handleEditBooking}
          onView={handleViewBooking}
        />

        <DayEventsModal
          isOpen={dayEventsModal.open}
          date={dayEventsModal.date}
          events={dayEventsModal.events}
          onClose={() => setDayEventsModal({ open: false })}
          onEventClick={(ev) => {
            setSelectedEvent(ev);
            setIsModalOpen(true);
            setDayEventsModal({ open: false });
          }}
        />
      </div>
    </div>
  );
}
